import { ChangeDetectionStrategy, Component, computed, effect, input, OnInit, Signal, viewChild } from '@angular/core';
import { NODE_EDIT_TAB_COMPONENT, NodeEditTab, SelectedNodeForEdit } from 'src/app/shared/flow-chart/flow-chart.model';
import { AlertTriggerNodeData } from '../../../node-edit.type';
import { FormControl, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { AlertV2Document } from '@models/alerts-v2.model';
import { AlarmSelectors } from '@states/alarm/alarm.selector-types';
import { AlarmActions } from '@states/alarm/alarm.action-types';
import { toSignal } from '@angular/core/rxjs-interop';
import { startWith } from 'rxjs';
import { smartSearch } from 'src/app/helpers/common.helpers';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { EventCameraSnapshotComponent } from 'src/app/pages/alerts-v2/pages/alert-main-v2/components/event-camera-snapshot/event-camera-snapshot.component';

@Component({
  selector: 'edit-alerts',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    MatSelect,
    MatOption,
    MatTooltip,
    EventCameraSnapshotComponent,
  ],
  templateUrl: './edit-alerts.component.html',
  styleUrl: './edit-alerts.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NODE_EDIT_TAB_COMPONENT,
      useExisting: EditAlertsComponent,
    },
  ],
})
export class EditAlertsComponent implements OnInit, NodeEditTab {
  public selectedNodeForEdit = input<SelectedNodeForEdit<AlertTriggerNodeData>>();
  public disableForm = input<boolean>(false);

  public searchQuery = new FormControl('');

  public form: UntypedFormGroup = this.fb.group({
    name: [
      this.translateService.instant('FLOW_CHART.NODES.ALERTTRIGGER.NAME'),
      [Validators.required],
    ],
    eventId: [
      null,
      [Validators.required]
    ],
  });

  public selectEventsSig: Signal<AlertV2Document[]> = this.store$.selectSignal(AlarmSelectors.selectAlertEvents);

  public readonly searchProperty = "name";
  public readonly valueProperty = "_id";

  public searchQuerySig = toSignal(
    this.searchQuery.valueChanges.pipe(
      startWith(this.searchQuery.value)
    ),
    { initialValue: '' }
  );

  public filteredItemsSig: Signal<Map<string, boolean>> = computed(() => {
    const searchQuery = this.searchQuerySig().toLowerCase();
    const options = this.selectEventsSig();

    let filtered;

    filtered = smartSearch(options, searchQuery, this.searchProperty) as AlertV2Document[];

    return new Map<string, boolean>(filtered.map((item) => [item[this.valueProperty], true]));
  })

  public optionsMapSig: Signal<Record<string, any>> = computed(() => {
    const options = this.selectEventsSig();
    const optionsMap: Record<string, any> = {};

    options.forEach((value) => {
      const valPropVal = value[this.valueProperty];
      optionsMap[valPropVal] = value;
    });

    return optionsMap;
  })

  constructor(
    private fb: UntypedFormBuilder,
    private translateService: TranslateService,
    private store$: Store,
  ) {
    effect(() => {
      const events = this.selectEventsSig();

      if (!events?.length) {
        this.form.controls['eventId']?.disable();
      } else {
        this.form.controls['eventId']?.enable();
      }
    })
  }

  public ngOnInit(): void {
    if (this.disableForm()) {
      this.form.disable();
    }

    if (this.selectedNodeForEdit()?.data?.name) {
      this.form.patchValue({
        name: this.selectedNodeForEdit().data.name,
        eventId: this.selectedNodeForEdit().data.eventId,
      })
    }

    if (!this.selectEventsSig()?.length) {
      this.store$.dispatch(AlarmActions.resetToInitialState());
      this.store$.dispatch(AlarmActions.getAlerts());
    }
  }

  public onPanelOpen(opened: boolean, input: HTMLInputElement): void {
    if (opened) {
      this.searchQuery.setValue('');

      setTimeout(() => {
        input.focus();
      });
    }
  }

  public clear(): void {
    this.searchQuery.setValue('');
  }

  getValue(item: any, prop?: string): any {
    if (!prop) return item;

    return prop.split('.').reduce((acc, key) => acc?.[key], item);
  }
}
